import { useCallback, useEffect, useRef } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type {
  TrackDefinition,
  TrackResult,
} from '@internal/backstage-plugin-pulse-common';
import {
  parseEntityRef,
  stringifyEntityRef,
  type Entity,
} from '@backstage/catalog-model';
import { trackResultsResponseSchema } from '../schemas/tracks';
import {
  PULSE_CACHE_KEYS,
  PULSE_QUERY_CACHE_POLICIES,
  useCachedQuery,
} from './cache';
import { usePulseTracks } from './use-pulse-tracks';

type CatalogApiLike = {
  getEntitiesByRefs(options: {
    entityRefs: string[];
  }): Promise<{ items: Array<Entity | undefined> }>;
};

const toCanonicalEntityRef = (entityRef: string): string =>
  stringifyEntityRef(
    parseEntityRef(entityRef, {
      defaultKind: 'component',
      defaultNamespace: 'default',
    }),
  );

export type TrackResultRow = TrackResult & { entity?: Entity };

export type TrackDetailsData = {
  track: TrackDefinition;
  results: TrackResultRow[];
};

export const useTrackDetails = (trackId: string) => {
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef) as CatalogApiLike;
  const {
    value: tracks,
    loading: tracksLoading,
    refreshing: tracksRefreshing,
    error: tracksError,
    retry: retryTracks,
  } = usePulseTracks();
  const track = tracks?.find(candidate => candidate.id === trackId);
  const refreshedMissingTrackRef = useRef<string>();
  const shouldRefreshMissingTrack = Boolean(
    tracks && !track && refreshedMissingTrackRef.current !== trackId,
  );

  useEffect(() => {
    if (!shouldRefreshMissingTrack) {
      return;
    }

    refreshedMissingTrackRef.current = trackId;
    void retryTracks();
  }, [retryTracks, shouldRefreshMissingTrack, trackId]);

  const fetchTrackDetails = useCallback(async () => {
    const response = await fetchApi.fetch(
      `plugin://pulse/tracks/${encodeURIComponent(trackId)}`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to fetch track: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    const parsed = trackResultsResponseSchema.parse(data);

    const normalizedEntityRefByResultRef = new Map(
      parsed.results.map(result => [
        result.entityRef,
        toCanonicalEntityRef(result.entityRef),
      ]),
    );
    const canonicalEntityRefs = Array.from(
      new Set(normalizedEntityRefByResultRef.values()),
    );

    let entities: Entity[] = [];
    if (canonicalEntityRefs.length > 0) {
      const catalogResponse = await catalogApi.getEntitiesByRefs({
        entityRefs: canonicalEntityRefs,
      });
      entities = catalogResponse.items.filter(
        (item: Entity | undefined): item is Entity => item !== undefined,
      );
    }

    const entityByRef = new Map(
      entities.map(entity => [stringifyEntityRef(entity), entity]),
    );

    const results: TrackResultRow[] = parsed.results.map(result => ({
      ...result,
      entity: entityByRef.get(
        normalizedEntityRefByResultRef.get(result.entityRef) ??
          result.entityRef,
      ),
    }));

    return {
      track: parsed.track,
      results,
    };
  }, [catalogApi, fetchApi, trackId]);

  const detailsQuery = useCachedQuery({
    cacheKey: PULSE_CACHE_KEYS.trackDetails(trackId),
    fetcher: fetchTrackDetails,
    ...PULSE_QUERY_CACHE_POLICIES.details,
    enabled: Boolean(track),
  });

  return {
    ...detailsQuery,
    track,
    loading:
      tracksLoading ||
      (!track && (tracksRefreshing || shouldRefreshMissingTrack)) ||
      detailsQuery.loading,
    error: tracksError ?? detailsQuery.error,
  };
};
